<script setup>
import Button from './Components/Button.vue';
import { computed, onMounted, ref } from 'vue';
import { Head } from "@inertiajs/vue3";
import Subfooter from './Includes/Subfooter.vue';
import faq from './Components/Faq.vue';
import Gallery from './Components/Gallery.vue';
import LeaseCalculator from './Components/LeaseCalculator.vue';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue'
import moment from 'moment/moment';
import CustomLink from './Components/CustomLink.vue';

const showFinancing = ref(true);

const props = defineProps({
    vehicle: Object
})

const vehicleImages = computed(() => {
    return props.vehicle.images.map(image => {
        return {
            src: `/storage/images/${image}`,
            alt: props.vehicle.title
        }
    })
})

const tabs = [
    { name: 'Details', href: '#', current: true },
    { name: 'Opties & Accessoires', href: '#', current: false },
    { name: 'Extra informatie', href: '#', current: false },
]

const eurFormatter = new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
})

//in the lease-auto-calculator div, i want the current html from class lp-result-euro
const currentLeasePrice = ref(0);

// Function to decode HTML entities
function decodeHtmlEntities(text) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
}

onMounted(() => {
    //get the current lease price from the html
    setTimeout(() => {

        document.dispatchEvent(new Event('DOMContentLoaded'));

        const rawHtml = document.querySelector('.lp-result-euro').innerHTML;
        currentLeasePrice.value = decodeHtmlEntities(rawHtml);
    }, 100);
})
</script>

<template>
    <Head>
        <title>{{ vehicle.title }} - Zwagers Automotive</title>
        <meta name="description" content="Ontdek de {{ vehicle.title }} bij Zwagers Automotive. Deze betrouwbare, gebruikte auto biedt hoogwaardige specificaties, uitstekende prestaties en comfort. Plan vandaag nog een proefrit!">
        <meta name="robots" content="index, follow" />

        <!-- Open Graph voor social media weergave -->
        <meta property="og:title" content="{{ vehicle.title }} - Zwagers Automotive" />
        <meta property="og:description" content="Bekijk de gedetailleerde informatie over deze {{ vehicle.title }}. Plan een proefrit bij Zwagers Automotive en ervaar deze hoogwaardige occasion zelf." />
        <meta property="og:type" content="website" />

        <!-- Twitter Cards voor betere zichtbaarheid op Twitter -->
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="{{ vehicle.title }} - Zwagers Automotive" />
        <meta name="twitter:description"
            content="Ontdek {{ vehicle.brand }} {{ vehicle.model }} bij Zwagers Automotive. Betrouwbare en gebruikte auto met volledige specificaties en foto's. Boek direct een proefrit!" />
    </Head>


    <div class="fixed flex items-end p-8 justify-center h-screen w-screen overflow-hidden z-40 pointer-events-none"
        v-auto-animate>
        <div v-if="showFinancing"
            class="bg-white max-w-2xl p-4 rounded-lg shadow-lg ring-1 ring-black pointer-events-auto">
            <div class="flex w-full justify-between gap-8">
                <p class="font-semibold">Financieringsmogelijkheden?</p>
                <font-awesome-icon :icon="['far', 'xmark']" class="cursor-pointer p-2" @click="showFinancing = false" />
            </div>
            <p class="text-base mt-2">Benieuwd naar de financieringsopties voor dit voertuig?<br>Gebruik onze
                financieringscalculator om eenvoudig je maandelijkse kosten te berekenen.</p>
            <div class="flex justify-center w-full text-center pt-6">
                <CustomLink @click="showFinancing = false" :useInertia="false" href="#calc" class="primary-button">Naar
                    de financieringscalculator</CustomLink>
            </div>
        </div>
    </div>

    <section class="flex flex-col bg-black text-white pt-16 md:pt-0 md:-space-y-56 lg:-space-y-80">
        <div class="z-20 h-full bg-gradient-to-b from-black/5 to-black">
            <!--image 1-->
            <div :style="{ backgroundImage: 'url(/storage/images/' + vehicle.images[0] + ')', }"
                class="relative h-auto md:h-screen flex flex-col max-h-[1200px] bg-center bg-cover w-full bg-opacity-45 aspect-video">
                <div class="z-20 flex justify-center h-full w-full">
                    <div class="w-11/12 px:0 md:px-6 xl:px-0 h-5/6 max-w-6xl">
                    </div>
                </div>
                <div class="absolute h-24 bg-gradient-to-b from-black/5 from-35% to-black bottom-0 left-0 right-0 w-full">
                </div>
            </div>
        </div>

        <div id="subhero"
            class="flex justify-center py-8 lg:py-28 z-20 bg-gradient-to-b from-transparent via-black via-35% to-black text-white">
            <div class="w-11/12 px:0 md:px-6 xl:px-0 flex flex-col gap-4 md:gap-6 lg:gap-10 max-w-6xl">
                <h3 class="flex flex-col gap-4 pb-6 md:text-right truncate">{{ vehicle.title.split('|')[0] }}</h3>
                <div class="flex flex-col lg:flex-row gap-8 md:gap-12 lg:gap-20">
                    <div
                        class="flex flex-row items-start shrink-0 order-last lg:order-first gap-10 self-stretch pb-4 md:pb-6 lg:pb-0">
                        <div class="flex items-start flex-col self-stretch">
                            <p class="font-bold" v-if="vehicle.top_speed">Topsnelheid</p>
                            <p class="font-bold" v-if="vehicle.acceleration">Acceleratie</p>
                            <p class="font-bold" v-if="vehicle.power_hp && vehicle.power_kw">Motorvermogen</p>
                        </div>
                        <div class="flex items-start flex-col self-stretch">
                            <p v-if="vehicle.top_speed">{{ vehicle.top_speed }} km/u</p>
                            <p v-if="vehicle.acceleration">0-100 {{ vehicle.acceleration }} sec</p>
                            <p v-if="vehicle.power_hp && vehicle.power_kw">{{ vehicle.power_hp }} pk / {{
                                vehicle.power_kw }} kW</p>
                        </div>
                    </div>
                    <p class="text-pretty">Rijplezier begint bij Zwagers Automotive
                        Bij Zwagers Automotive in Borne vind je zorgvuldig geselecteerde auto’s die
                        klaarstaan om jou de ultieme rijervaring te geven. Met persoonlijke aandacht
                        en vertrouwd advies helpen wij je om jouw rijwens waar te maken. 
                        Bekijk ons aanbod hieronder en ontdek het zelf.
                    </p>
                </div>
                <div class="gap-10 grid-cols-1 grid sm:grid-cols-3 mx-auto text-left text-nowrap text-white w-full">
                    <div class="flex flex-col justify-start" v-if="vehicle.year">
                        <div class="flex items-center gap-4 justify-center">
                            <font-awesome-icon class="h-8" :icon="['fas', 'calendar-week']" />
                            <span class="text-3xl font-semibold tracking-tight lg:text-4xl">{{ vehicle.year }}</span>
                        </div>
                        <span class="text-base/7 text-gray-400 text-center">Bouwjaar</span>
                    </div>
                    <div class="flex flex-col justify-start lg:justify-end" v-if="vehicle.transmission">
                        <div class="flex items-center gap-4 justify-center">
                            <font-awesome-icon class="h-8"
                                :icon="['fas', vehicle.mileage < 80000 ? 'gauge-low' : vehicle.mileage < 150000 ? 'gauge' : 'gauge-high']" />
                            <span class="text-3xl font-semibold tracking-tight lg:text-4xl">{{
                                vehicle.mileage.toLocaleString() }}</span>
                        </div>
                        <span class="text-base/7 text-gray-400 text-center">Kilometerstand</span>
                    </div>
                    <div class="flex flex-col justify-start lg:justify-end" v-if="vehicle.transmission">
                        <div class="flex items-center gap-4 justify-center">
                            <svg width="32" height="32" viewBox="0 0 13 13" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd"
                                    d="M10.7426 10.7426C9.61742 11.8679 8.0913 12.5 6.5 12.5C4.9087 12.5 3.38258 11.8679 2.25736 10.7426C1.13214 9.61742 0.5 8.0913 0.5 6.5C0.5 4.9087 1.13214 3.38258 2.25736 2.25736C3.38258 1.13214 4.9087 0.5 6.5 0.5C8.0913 0.5 9.61742 1.13214 10.7426 2.25736C11.8679 3.38258 12.5 4.9087 12.5 6.5C12.5 8.0913 11.8679 9.61742 10.7426 10.7426ZM7.25 4.25C7.25 3.83516 6.91484 3.5 6.5 3.5C6.08516 3.5 5.75 3.83516 5.75 4.25V5.75H5L5 4.25C5 3.83516 4.66484 3.5 4.25 3.5C3.83516 3.5 3.5 3.83516 3.5 4.25L3.5 8.75C3.5 9.16484 3.83516 9.5 4.25 9.5C4.66484 9.5 5 9.16484 5 8.75V7.25L5.75 7.25V8.75C5.75 9.16485 6.08516 9.5 6.5 9.5C6.91484 9.5 7.25 9.16485 7.25 8.75V7.25H8.75C9.16484 7.25 9.5 6.91484 9.5 6.5V4.25C9.5 3.83516 9.16484 3.5 8.75 3.5C8.33516 3.5 8 3.83516 8 4.25L8 5.75H7.25V4.25Z"
                                    fill="white" />
                            </svg>
                            <span class="text-3xl font-semibold tracking-tight lg:text-4xl">{{ vehicle.transmission
                                }}</span>
                        </div>
                        <span class="text-base/7 text-gray-400 text-center">Transmissie</span>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <main>
        <section class="flex w-11/12 px:0 md:px-6 xl:px-0 max-w-6xl py-12 md:py-16 lg:py-20 mx-auto flex-col gap-12">
            <Gallery :images="vehicleImages" />
            <section class="grid grid-cols-1 lg:grid-cols-3 gap-y-8 lg:gap-x-8 pb-8">
                <div class="flex flex-col col-span-2 gap-6">
                    <div class="flex flex-col gap-1">
                        <h3 class="md:text-left text-balance truncate pt-4 sm:pt-8 md:pt-0">{{ vehicle.title.split('|')[0] }}</h3>
                        <h5 v-if="vehicle.title.includes('|')" class="text-black">{{ vehicle.title.split('|').slice(1).join(' | ').trim() }}</h5>
                    </div>
                    <p class="text-balance" v-html="vehicle.ad_text"></p>
                </div>
                <div class="h-full">
                    <div class="sticky bottom-0 top-auto md:bottom-auto md:top-28 flex flex-col sm:flex-row lg:flex-col gap-y-2 gap-x-20 justify-between lg:justify-normal items-start sm:items-center lg:items-start">
                        <div class="flex flex-row sm:flex-col justify-between items-center sm:items-start w-full gap-1 pb-2">
                            <h2 class="text-tan">{{ eurFormatter.format(vehicle.price).replace(',00', ',-') }}</h2>
                            <div class="flex flex-col lg:gap-1 items-baseline lg:flex-row">
                                <div class="flex flex-row gap-2 items-center ">
                                    <font-awesome-icon class="h-3.5" :icon="['fas', 'hand-holding-circle-dollar']" />
                                    <h4 class="lg:text-xl font-semibold">
                                        {{ currentLeasePrice }}<span class="lg:text-base"> p/m</span>
                                    </h4>
                                </div>
                                <p class="text-sm sm:text-base/4 lg:text-base text-nowrap">op basis van financiering</p>
                            </div>
                        </div>
                        <div class="flex flex-col gap-2 w-full sm:max-w-sm">
                            <CustomLink href="#calc" :useInertia="false" class="secondary-black-button">Financiering
                                berekenen</CustomLink>
                            <div class="grid grid-cols-2 gap-2">
                                <CustomLink href="/overons#contactform" class="primary-button">
                                    Contact
                                </CustomLink>
                                <CustomLink href="https://wa.me/+31617328160" target="_blank" :useInertia="false"
                                    class="primary-button">
                                    <font-awesome-icon :icon="['fab', 'whatsapp']" class="h-5 w-5" />
                                    Whatsapp
                                </CustomLink>
                            </div>
                            <Button class="secondary-black-button" type="href"
                                :href="'/overons?subject=Proefrit&vehicle=' + vehicle.brand + ' ' + vehicle.model + ' ' + vehicle.type.replace(/\|/g, '-') + '#contactform'">Proefrit
                                aanvragen</Button>
                        </div>
                    </div>
                </div>
            </section>
            <section class="space-y-8">
                <TabGroup>
                    <TabList>
                        <div class="flex flex-row flex-nowrap justify-between sm:justify-normal sm:gap-2">
                            <Tab v-for="tab in tabs" :key="tab.name" :tab="tab" v-slot="{ selected }">
                                <a :class="[selected ? 'border-tan text-tan font-bold' : 'focus:!outline-none border-transparent text-gray-700 hover:border-gray-300 hover:text-gray-500 font-medium sm:font-bold', 'whitespace-nowrap border-b-2 p-1 sm:p-1.5 md:p-2.5 lg:p-4 text-base/4 sm:text-base/6 md:text-base transition duration-500']">{{ tab.name }}</a>
                            </Tab>
                        </div>
                    </TabList>
                    <TabPanels>
                        <TabPanel>
                            <div
                                class="flex flex-col items-center md:items-start md:flex-row py-2 border-b-[1px] border-black justify-between">
                                <div class="flex py-0 items-center gap-3 w-full">
                                    <font-awesome-icon :icon="['far', 'circle-info']" class="h-7 w-7" />
                                    <p class="font-bold">Basis gegevens</p>
                                </div>
                                <div class="flex w-full pt-7 md:pt-0">
                                    <div class="flex flex-col gap-1 w-1/2">
                                        <p v-if="vehicle.body">Carrosserietype</p>
                                        <p>Categorie</p>
                                        <p v-if="vehicle.transmission">Aandrijving</p>
                                        <p v-if="vehicle.seats">Stoelen</p>
                                        <p v-if="vehicle.doors">Deuren</p>
                                        <p v-if="vehicle.license_plate">Kenteken</p>
                                        <p v-if="vehicle.tax">Belasting</p>
                                    </div>
                                    <div class="flex flex-col gap-1 w-1/2">
                                        <p v-if="vehicle.body">{{ vehicle.body }}</p>
                                        <p>{{ vehicle.new ? 'Nieuw' : 'Gebruikt' }}</p>
                                        <p v-if="vehicle.transmission">{{ vehicle.transmission }}</p>
                                        <p v-if="vehicle.seats"> {{ vehicle.seats }}</p>
                                        <p v-if="vehicle.doors">{{ vehicle.doors }}</p>
                                        <p v-if="vehicle.license_plate">{{ vehicle.license_plate }}</p>
                                        <p v-if="vehicle.tax">€{{ vehicle.tax.split(' - ')[0] }} - €{{
                                            vehicle.tax.split(' - ')[1] }} per kwartaal</p>
                                    </div>
                                </div>
                            </div>
                            <div
                                class="flex flex-col items-center md:items-start md:flex-row py-2 border-b-[1px] border-black justify-between">
                                <div class="flex py-0 items-center gap-3 w-full">
                                    <font-awesome-icon :icon="['far', 'gauge']" class="h-7 w-7" />
                                    <p class="font-bold">Voertuig gegevens</p>
                                </div>
                                <div class="flex w-full pt-7 md:pt-0">
                                    <div class="flex flex-col gap-1 w-1/2">
                                        <p v-if="vehicle.mileage">Kilometerstand</p>
                                        <p v-if="vehicle.year">Bouwjaar</p>
                                        <p v-if="vehicle.apk">APK</p>
                                        <p>BTW auto</p>
                                    </div>
                                    <div class="flex flex-col gap-1 w-1/2">
                                        <p v-if="vehicle.mileage">{{ vehicle.mileage.toLocaleString() }} km</p>
                                        <p v-if="vehicle.year">{{ vehicle.year }}</p>
                                        <p v-if="vehicle.apk">{{ moment(vehicle.apk).format('DD-MM-YYYY') }}</p>
                                        <p>{{ vehicle.marge ? 'Nee' : 'Ja' }}</p>
                                    </div>
                                </div>
                            </div>
                            <div
                                class="flex flex-col items-center md:items-start md:flex-row py-2 border-b-[1px] border-black justify-between">
                                <div class="flex py-0 items-center gap-3 w-full">
                                    <font-awesome-icon :icon="['far', 'engine']" class="h-7 w-7" />
                                    <p class="font-bold break-keep">Technische gegevens</p>
                                </div>
                                <div class="flex w-full pt-7 md:pt-0">
                                    <div class="flex flex-col gap-1 w-1/2">
                                        <p v-if="vehicle.top_speed">Topsnelheid</p>
                                        <p v-if="vehicle.acceleration">Acceleratie</p>
                                        <p v-if="vehicle.power_kw && vehicle.power_hp">Motorvermogen</p>
                                        <p v-if="vehicle.transmission">Transmissie</p>
                                        <p v-if="vehicle.cylinder_capacity">Cilinderinhoud</p>
                                        <p v-if="vehicle.cylinders">Cilinders</p>
                                        <p v-if="vehicle.weight">Ledig gewicht</p>
                                    </div>
                                    <div class="flex flex-col gap-1 w-1/2">
                                        <p v-if="vehicle.top_speed">{{ vehicle.top_speed }} km/u</p>
                                        <p v-if="vehicle.acceleration">0-100 {{ vehicle.acceleration }} sec</p>
                                        <p v-if="vehicle.power_kw && vehicle.power_hp">{{ vehicle.power_kw }} kW / {{
                                            vehicle.power_hp }} pk</p>
                                        <p v-if="vehicle.transmission">{{ vehicle.transmission }}</p>
                                        <p v-if="vehicle.cylinder_capacity">{{ vehicle.cylinder_capacity }} cm³</p>
                                        <p v-if="vehicle.cylinders">{{ vehicle.cylinders }}</p>
                                        <p v-if="vehicle.weight">{{ vehicle.weight }} kg</p>
                                    </div>
                                </div>
                            </div>
                            <div
                                class="flex flex-col items-center md:items-start md:flex-row py-2 border-b-[1px] border-black justify-between">
                                <div class="flex py-0 items-center gap-3 w-full">
                                    <font-awesome-icon :icon="['far', 'leaf']" class="h-7 w-7" />
                                    <p class="font-bold break-keep">Energie verbruik</p>
                                </div>
                                <div class="flex w-full pt-7 md:pt-0">
                                    <div class="flex flex-col gap-1 w-1/2">
                                        <p v-if="vehicle.fuel">Brandstof</p>
                                        <p v-if="vehicle.consumption">Brandstofverbruik</p>
                                        <p v-if="vehicle.co2_emission">CO2-emissie</p>
                                        <p v-if="vehicle.emission_class">Emissieklasse</p>
                                    </div>
                                    <div class="flex flex-col  gap-1 w-1/2">
                                        <p v-if="vehicle.fuel">{{ vehicle.fuel }}</p>
                                        <p v-if="vehicle.consumption">{{ vehicle.consumption }} L/ 100km (gem.)</p>
                                        <p v-if="vehicle.co2_emission">{{ vehicle.co2_emission }} g/km (gem.)</p>
                                        <p v-if="vehicle.emission_class">Euro {{ vehicle.emission_class }}</p>
                                    </div>
                                </div>
                            </div>
                            <div
                                class="flex flex-col items-center md:items-start md:flex-row py-2 border-b-[0px] border-black justify-between">
                                <div class="flex py-0 items-center gap-3 w-full">
                                    <font-awesome-icon :icon="['far', 'swatchbook']" class="h-7 w-7" />
                                    <p class="font-bold text-nowrap">Kleur en interieur</p>
                                </div>
                                <div class="flex w-full pt-7 md:pt-0">
                                    <div class="flex flex-col gap-1 w-1/2">
                                        <p v-if="vehicle.color">Kleur</p>
                                        <p v-if="vehicle.paint_type">Soort kleur</p>
                                        <p v-if="vehicle.interior_color">Kleur interieur</p>
                                        <p v-if="vehicle.upholstery" class="text-nowrap">Materiaal interieur</p>
                                    </div>
                                    <div class="flex flex-col gap-1 w-1/2">
                                        <p v-if="vehicle.color">{{ vehicle.color.charAt(0).toUpperCase() +
                                            vehicle.color.slice(1) }}</p>
                                        <p v-if="vehicle.paint_type">{{ vehicle.paint_type.charAt(0).toUpperCase() +
                                            vehicle.paint_type.slice(1) }}</p>
                                        <p v-if="vehicle.interior_color">{{ vehicle.interior_color }}</p>
                                        <p v-if="vehicle.upholstery">{{ vehicle.upholstery }}</p>
                                    </div>
                                </div>
                            </div>
                        </TabPanel>
                        <TabPanel>
                            <div v-for="option in vehicle.accessories" class="inline-flex">
                                <div class="border rounded-lg px-6 py-3 text-center mx-1 my-1.5">{{ option }}</div>
                            </div>
                        </TabPanel>
                        <TabPanel>
                            <div class="inline-flex">
                                <p v-html="vehicle.note" class="text-balance"></p>
                            </div>
                        </TabPanel>
                    </TabPanels>

                </TabGroup>
            </section>
            <section class="flex flex-col sm:grid grid-cols-3 gap-8 pb-8 items-center">
                <div class="flex flex-col col-span-2 gap-6">
                    <h3 class="md:text-left text-balance">Vraag nu een proefrit aan!</h3>
                    <p>Neem direct contact met ons op indien u intresse heeft in de {{ vehicle.brand }} {{ vehicle.model
                        }}</p>
                </div>
                <div class="grid grid-cols-2 gap-2 w-full">
                    <CustomLink href="/overons#contactform" class="primary-button">
                        Contact
                    </CustomLink>
                    <CustomLink href="https://wa.me/+31617328160" target="_blank" :useInertia="false"
                        class="primary-button">
                        <font-awesome-icon :icon="['fab', 'whatsapp']" class="h-5 w-5" />
                        Whatsapp
                    </CustomLink>
                </div>
            </section>
        </section>

        <LeaseCalculator :vehicle="vehicle" id="calc" />

        <faq />
        <subfooter />
    </main>
</template>
